import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { imageUrl } from "@/lib/blog";

/**
 * Image picker + uploader. Returns the storage path via onChange.
 * Shows a thumbnail preview and lets the user replace or clear it.
 */
export function ImageUpload({ value, alt = "", onChange, testid = "image-upload", label = "Image" }) {
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const token = localStorage.getItem("fra_admin_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${base}/api/admin/blog/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Upload failed (${resp.status})`);
      }
      const data = await resp.json();
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.message || errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <p className="label-caps mb-1.5">{label}</p>
      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <img src={imageUrl(value)} alt={alt} className="aspect-[16/9] w-full object-cover" />
          <button
            onClick={() => onChange(null)}
            aria-label="Remove image"
            data-testid={`${testid}-clear`}
            className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-slate-700 shadow hover:bg-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          data-testid={`${testid}-pick`}
          disabled={busy}
          className="flex aspect-[16/9] w-full items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : (
            <span className="flex flex-col items-center gap-1 text-sm">
              <Upload className="h-5 w-5" /> Click to upload (JPG/PNG/WebP, max 5MB)
            </span>
          )}
        </button>
      )}
      {value && (
        <button
          onClick={() => input.current?.click()}
          data-testid={`${testid}-replace`}
          disabled={busy}
          className="mt-2 text-xs font-medium text-slate-600 hover:text-slate-900"
        >
          {busy ? "Uploading..." : "Replace image"}
        </button>
      )}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} />
    </div>
  );
}
